import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { TransportOverview } from "../../components/transport/overview";
import { NocTripsView, ProviderTripsView, nocTripsSearchShape, providerTripsSearchShape } from "../../components/transport-trips";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { useDefaultGamesId } from "../../lib/competition/games";
import { transportView } from "../../lib/transport/access";
import { isYmd, todayIn } from "../../lib/travel/view";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * `/logistics/transportation` — what it shows depends on who asks
 * (`transportView`): the dispatch desk's Overview, a provider's trips on its
 * vehicles, or a NOC's own trips with "Request a trip" (S5's views). Every
 * view's URL state is lenient: a hand-edited value falls back to "not set".
 *   day        the Overview's day (games' zone; absent = today)
 *   transfers  the Suggested transfers switch (absent = arrivals)
 *   leg        a deep link from a record ("Arrange transport"): that
 *              person's leg is found, highlighted and its group picked
 *   journey    a deep link from an A&D journey: its groups are highlighted
 */
export const Route = createFileRoute("/_app/logistics/transportation/")({
	validateSearch: z.object({
		day: lenient(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
		transfers: lenient(z.enum(["arrivals", "departures"])),
		leg: lenient(z.string().uuid()),
		journey: lenient(z.string().min(1).max(200)),
		...nocTripsSearchShape,
		...providerTripsSearchShape,
	}),
	component: TransportIndex,
});

function TransportIndex() {
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	const view = transportView(session);

	if (!gamesId || !timeZone)
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[16rem] rounded-[0.625rem]" />
			</div>
		);
	if (view === "provider") return <ProviderTripsView session={session} gamesId={gamesId} timeZone={timeZone} search={search} navigate={navigate} />;
	if (view === "noc") return <NocTripsView session={session} gamesId={gamesId} timeZone={timeZone} search={search} navigate={navigate} />;

	const today = todayIn(timeZone);
	return (
		<TransportOverview
			session={session}
			gamesId={gamesId}
			gamesIdInUrl={search.games_id}
			timeZone={timeZone}
			today={today}
			date={isYmd(search.day) ? search.day : today}
			onDate={(d) => void navigate({ search: (s) => ({ ...s, day: d === today ? undefined : d }), replace: true })}
			direction={search.transfers === "departures" ? "DEPARTURE" : "ARRIVAL"}
			onDirection={(d) => void navigate({ search: (s) => ({ ...s, transfers: d === "DEPARTURE" ? "departures" : undefined }), replace: true })}
			focus={{ leg: search.leg, journey: search.journey }}
			onClearFocus={() => void navigate({ search: (s) => ({ ...s, leg: undefined, journey: undefined }), replace: true })}
		/>
	);
}
